import axiosInstance from '../config/axios';

const getAuthHeader = (extraHeaders = {}) => {
  const token = localStorage.getItem('authToken');
  const validToken = token && token !== 'undefined' && token !== 'null' ? token : null;
  return {
    ...extraHeaders,
    ...(!validToken ? { _skipAuthRedirect: true } : {}),
    ...(validToken ? { Authorization: `Bearer ${validToken}` } : {})
  };
};

// ──────────────────────────────────────────────
// Classes APIs
// ──────────────────────────────────────────────

/**
 * Fetch all public classes (no auth required)
 */
export const getPublicClasses = () =>
  axiosInstance.get('/class/public', { headers: getAuthHeader({ _skipAuthRedirect: true }), _skipAuthRedirect: true })
    .then((res) => res.data)
    .catch(() => axiosInstance.get('/classes', { headers: getAuthHeader({ _skipAuthRedirect: true }), _skipAuthRedirect: true }).then((res) => res.data));

/**
 * Create a new class (auth required)
 * @param {object} data - FormData or object containing class details
 */
export const createClass = (data) =>
  axiosInstance.post('/classes', data, { headers: getAuthHeader() }).then((res) => res.data);

/**
 * Fetch class detail by ID (auth required)
 * @param {string} id
 */
export const getClassById = (id) =>
  axiosInstance.get(`/classes/${id}`, { headers: getAuthHeader({ _skipAuthRedirect: true }), _skipAuthRedirect: true }).then((res) => res.data);

export const updateClass = (id, data) =>
  axiosInstance.put(`/classes/${id}`, data).then((res) => res.data);

/**
 * Join / book a class (auth required)
 * @param {string} id
 * @param {object} data
 */
export const joinClass = (id, data = {}) =>
  axiosInstance.post(`/classes/${id}/join`, data).then((res) => res.data);

export const storeTransaction = (data) =>
  axiosInstance.post(`/store-transaction`, data).then((res) => res.data);

/**
 * Get my booked classes (auth required)
 */
export const getMyBookings = () =>
  axiosInstance.get('/classes/my-bookings').then((res) => res.data);

export const getMyClasses = () =>
  axiosInstance.get('/classes/my-classes').then((res) => res.data);

export const getCreatedClasses = () =>
  axiosInstance.get('/classes/created-by-me').then((res) => res.data);

// ──────────────────────────────────────────────
// Class Management (Attendance, Resources, etc.)
// ──────────────────────────────────────────────

// Students
export const getClassStudents = (classId, params = {}) =>
  axiosInstance.get(`/students/${classId}`, { params }).then((res) => res.data);

export const searchClassStudents = (classId, params = {}) =>
  axiosInstance.get(`/search-students/${classId}`, { params }).then((res) => res.data);

export const assignClassStudent = (classId, data) =>
  axiosInstance.post(`/classes/${classId}/assign`, data).then((res) => res.data);

export const unassignClassStudent = (classId, data) =>
  axiosInstance.post(`/classes/${classId}/unassign`, data).then((res) => res.data);

// Attendance
export const markAttendance = (data) => axiosInstance.post('/mark-attendance', data).then((res) => res.data);
export const getAttendance = (params) => {
  if (typeof params === 'object') {
    return axiosInstance.get('/attendance', { params }).then((res) => res.data);
  }
  return axiosInstance.get(`/attendance?class_id=${params}`).then((res) => res.data);
};
export const exportAttendance = (params) => {
  if (typeof params === 'object') {
    return axiosInstance.get('/attendance/export', { params }).then((res) => res.data);
  }
  return axiosInstance.get(`/attendance/export?class_id=${params}`).then((res) => res.data);
};

// Class WorkBook
export const createClassWorkBook = (data) => axiosInstance.post('/create-class-workbook', data).then((res) => res.data);
export const getClassWorkBooks = (classId) => axiosInstance.get(`/list-class-workbook/${classId}`).then((res) => res.data);
export const deleteClassWorkBook = (id) => axiosInstance.delete(`/delete-class-workbook/${id}`).then((res) => res.data);

// Class Video
export const addClassVideo = (data) => axiosInstance.post('/add-class-video', data).then((res) => res.data);
export const listClassVideos = (classId) => axiosInstance.get(`/list-class-videos/${classId}`).then((res) => res.data);
export const deleteClassVideo = (id) => axiosInstance.delete(`/delete-class-video/${id}`).then((res) => res.data);

// Class HomeWork
export const addClassHomeWork = (data) => axiosInstance.post('/class-homework/add', data).then((res) => res.data);
export const listClassHomeWork = (classId) => axiosInstance.get(`/class-homework/list/${classId}`).then((res) => res.data);
export const deleteClassHomeWork = (id) => axiosInstance.delete(`/class-homework/delete/${id}`).then((res) => res.data);
export const getClassHomeWorkAnswers = (id) => axiosInstance.get(`/class-homework/answers/${id}`).then((res) => res.data);
export const submitClassHomeWorkAnswer = (data) => axiosInstance.post('/class-homework/answer', data).then((res) => res.data);

// Class Quiz
export const createClassQuiz = (data) => axiosInstance.post('/class-quiz', data).then((res) => res.data);
export const listClassQuiz = (classId) => axiosInstance.get(`/class-quiz/list?classId=${classId}`).then((res) => res.data);
export const getClassQuizById = (id) => axiosInstance.get(`/class-quiz/${id}`).then((res) => res.data);
export const deleteClassQuiz = (id) => axiosInstance.delete(`/class-quiz/${id}`).then((res) => res.data);
export const submitClassQuiz = (data) => axiosInstance.post('/class-quiz/submit', data).then((res) => res.data);

// Multiple Recording
export const addClassMultipleRecording = (data) => axiosInstance.post('/add-multiple-recording', data).then((res) => res.data);
export const listClassMultipleRecording = (classId) => axiosInstance.get(`/list-multiple-recording/${classId}`).then((res) => res.data);
export const deleteClassMultipleRecording = (id) => axiosInstance.delete(`/delete-class-multiple-recording/${id}`).then((res) => res.data);

// ──────────────────────────────────────────────
// Additional Class & Slot & Attendance APIs
// ──────────────────────────────────────────────

export const getClassPaymentStatusApi = (id) =>
  axiosInstance.get(`/classes/${id}/payment-status`).then((res) => res.data);

export const updateClassStatusApi = (id, data) =>
  axiosInstance.patch(`/classes/${id}/status`, data).then((res) => res.data);

export const deleteClassApi = (id) =>
  axiosInstance.delete(`/classes/${id}`).then((res) => res.data);

export const getAvailableSlotsApi = (classId) =>
  axiosInstance.get(`/classes/${classId}/available-slots`).then((res) => res.data);

export const bookSlotApi = (slotId, data = {}) =>
  axiosInstance.post(`/classes/slots/${slotId}/book`, data).then((res) => res.data);

export const cancelSlotApi = (slotId, data = {}) =>
  axiosInstance.post(`/classes/slots/${slotId}/cancel`, data).then((res) => res.data);

export const getClassRecordingByIdApi = (recordingId) =>
  axiosInstance.get(`/classes/recordings/${recordingId}`).then((res) => res.data);

export const updateAttendanceApi = (attendanceId, data) =>
  axiosInstance.put(`/attendance/${attendanceId}`, data).then((res) => res.data);

export const deleteAttendanceApi = (attendanceId) =>
  axiosInstance.delete(`/attendance/${attendanceId}`).then((res) => res.data);

export const getAttendanceSummaryApi = (id) =>
  axiosInstance.get(`/attendance/summary/${id}`).then((res) => res.data);

export const addClassWorkbooksApi = (id, data) =>
  axiosInstance.put(`/add-class-workbook/${id}`, data).then((res) => res.data);

export const updateWorkbookTitleApi = (id, workbookId, data) =>
  axiosInstance.put(`/update-title-class-workbook/${id}/${workbookId}`, data).then((res) => res.data);

export const deleteSingleWorkbookFileApi = (id, workbookId) =>
  axiosInstance.delete(`/delete-file-class-workbook/${id}/${workbookId}`).then((res) => res.data);

export const updateClassVideoApi = (id, data) =>
  axiosInstance.put(`/update-class-video/${id}`, data).then((res) => res.data);

export const updateClassHomeworkApi = (id, data) =>
  axiosInstance.put(`/class-homework/update/${id}`, data).then((res) => res.data);

export const updateClassQuizApi = (quizId, data) =>
  axiosInstance.put(`/class-quiz/${quizId}`, data).then((res) => res.data);

export const getClassQuizAnswersApi = (quizId) =>
  axiosInstance.get(`/class-quiz/answers/${quizId}`).then((res) => res.data);

export const updateMultipleRecordingApi = (recordingId, data) =>
  axiosInstance.put(`/update-multiple-recording/${recordingId}`, data).then((res) => res.data);

